import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import {
  calcResultByMonth,
  calcTotalGrossProfitYtd,
  calcTotalGrossProfitYtdThrough,
  calcTotalResultYtd,
  calcTotalResultYtdThrough,
  type YtdComparison,
  type PoTransaction,
  type ResultMonth,
} from "@/lib/kpi-calculations";

interface ResultResponse {
  success: boolean;
  transactions: PoTransaction[];
  yearsFetched: number;
  error?: string;
}

export interface TotalResultData extends YtdComparison {
  /** False hvis edge function feilet (PO API nede etc.). UI viser "—" og info. */
  available: boolean;
  /** Sentry-vennlig feilmelding for debug */
  error?: string;
  reconciled: YtdComparison | null;
  grossProfit: YtdComparison;
  grossProfitReconciled: YtdComparison | null;
  /** Resultat per måned i år, indeks 0 = januar. Tom når PowerOffice ikke svarte. */
  months: ResultMonth[];
}

/**
 * KPI #8 — Total resultat (P&L) for inneværende år med sammenligning vs fjor.
 * Erik-lock 2026-05-15. Henter AccountTransactions fra PowerOffice via
 * edge function `fetch-poweroffice-result`.
 *
 * Caches i 60 minutter (60 × 60 × 1000 ms staleTime) for å unngå hyppige
 * PO API-kall — månedsregnskap åpnes/lukkes ikke flere ganger per time.
 */
export function useTotalResultYtd(reconciledThroughIso?: string) {
  return useQuery({
    queryKey: ["total-result-ytd", reconciledThroughIso ?? "live"],
    queryFn: async (): Promise<TotalResultData> => {
      const { data, error } = await supabase.functions.invoke<ResultResponse>(
        "fetch-poweroffice-result",
        { method: "GET" },
      );

      if (error || !data?.success) {
        return {
          available: false,
          current: 0,
          previousYear: 0,
          deltaPct: null,
          reconciled: null,
          grossProfit: { current: 0, previousYear: 0, deltaPct: null },
          grossProfitReconciled: null,
          months: [],
          error: error?.message ?? data?.error ?? "Ukjent feil",
        };
      }

      const now = new Date();
      const result = calcTotalResultYtd(data.transactions, now);
      const grossProfit = calcTotalGrossProfitYtd(data.transactions, now);
      return {
        available: true,
        ...result,
        months: calcResultByMonth(data.transactions, now.getUTCFullYear()),
        grossProfit,
        reconciled: reconciledThroughIso
          ? calcTotalResultYtdThrough(data.transactions, now, reconciledThroughIso)
          : null,
        grossProfitReconciled: reconciledThroughIso
          ? calcTotalGrossProfitYtdThrough(data.transactions, now, reconciledThroughIso)
          : null,
      };
    },
    staleTime: 60 * 60 * 1000,
    retry: 1,
  });
}
